
import React, { useState, useEffect } from 'react';
import { Node } from 'reactflow';
import { NodeData, Member, NodeInspectorProps } from './types';
import { useEditorContext } from './EditorContext';
import { 
  X, Plus, FileText, Users, Search, 
  ChevronLeft, ChevronRight, Loader2,
  Layout,
  ToggleLeft, ToggleRight, Trash2, PauseCircle, Activity,
  Layers,
  User
} from 'lucide-react';

const NodeInspector: React.FC<NodeInspectorProps> = ({ node, readOnly, onUpdateNode, onClose }) => {
  const { onFetchMembers } = useEditorContext();
  const [activeTab, setActiveTab] = useState('General');

  // Define tabs dynamically based on feature availability
  const tabs = ['General', 'Status'];
  if (onFetchMembers) {
    tabs.push('Members');
  }

  if (!node) return null;

  return (
    <div className="w-[450px] bg-white border-l border-slate-200 flex flex-col h-full shadow-xl z-20">
      {/* Header */}
      <div className="p-5 pb-0 shrink-0">
        <div className="flex justify-between items-start mb-1">
          <h2 className="text-xl font-bold text-slate-900">Edit Group</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
            <X size={20} />
          </button>
        </div>
        <div className="text-[10px] text-slate-400 font-mono mb-4 truncate">
          ID: {node.id}
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200 gap-6">
          {tabs.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-3 text-sm font-medium transition-all relative ${
                activeTab === tab 
                  ? 'text-teal-600' 
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <span className="flex items-center gap-2">
                {tab === 'General' && <FileText size={14} />}
                {tab === 'Status' && <Activity size={14} />}
                {tab === 'Members' && <Users size={14} />}
                {tab}
              </span>
              {activeTab === tab && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-600 rounded-t-full" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-hidden bg-white">
        {activeTab === 'General' && (
          <GeneralTab node={node} readOnly={readOnly} onUpdateNode={onUpdateNode} />
        )}
        {activeTab === 'Status' && (
          <StatusTab node={node} readOnly={readOnly} onUpdateNode={onUpdateNode} />
        )}
        {activeTab === 'Members' && onFetchMembers && (
          <MembersTab node={node} readOnly={readOnly} />
        )}
      </div>
    </div>
  );
};

// --- Sub Components ---

const GeneralTab: React.FC<Omit<NodeInspectorProps, 'onClose'>> = ({ node, readOnly, onUpdateNode }) => {
  const { iconMap } = useEditorContext();
  const iconOptions = Object.keys(iconMap);

  if (!node) return null;
  const data = node.data;

  const handleAttrChange = (index: number, field: 'role' | 'value', val: string) => {
    const newAttrs = [...data.attributes];
    newAttrs[index][field] = val;
    onUpdateNode(node.id, { attributes: newAttrs });
  };

  const addAttr = () => {
    onUpdateNode(node.id, { attributes: [...data.attributes, { role: '', value: '' }] });
  };

  const removeAttr = (index: number) => {
      const newAttrs = data.attributes.filter((_, i) => i !== index);
      onUpdateNode(node.id, { attributes: newAttrs });
  };

  return (
    <div className="p-5 h-full overflow-y-auto">
      <div className="space-y-6">
        {/* Name Input */}
        <div className="space-y-1">
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Group Name</label>
          <input
            className="w-full p-2.5 text-sm border border-slate-200 rounded-lg focus:ring-1 focus:ring-teal-500 focus:border-teal-500 outline-none text-slate-700 transition-all font-medium"
            value={data.name}
            onChange={(e) => onUpdateNode(node.id, { name: e.target.value })}
            placeholder="Enter group name"
            readOnly={readOnly}
          />
        </div>

        {/* Icon Selection */}
        <div className="space-y-1">
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Icon</label>
            <div className="flex gap-2 flex-wrap">
                {iconOptions.map(icon => {
                    const IconComponent = iconMap[icon] || Layout;
                    const isSelected = data.iconPath === icon;
                    return (
                        <button
                            key={icon}
                            onClick={() => !readOnly && onUpdateNode(node.id, { iconPath: icon })}
                            className={`p-3 rounded-lg border transition-all flex items-center justify-center ${
                                isSelected 
                                ? 'bg-teal-50 border-teal-500 text-teal-600 shadow-sm ring-2 ring-teal-500/20' 
                                : 'border-slate-200 text-slate-400 hover:border-slate-300 hover:bg-slate-50'
                            }`}
                            title={icon}
                            type="button"
                        >
                           <IconComponent size={20} strokeWidth={isSelected ? 2.5 : 2} />
                        </button>
                    );
                })}
            </div>
        </div>

        {/* Description */}
        <div className="space-y-1">
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Description</label>
          <textarea
            className="w-full p-2.5 text-sm border border-slate-200 rounded-lg focus:ring-1 focus:ring-teal-500 focus:border-teal-500 outline-none text-slate-700 min-h-[100px] resize-none"
            value={data.description}
            onChange={(e) => onUpdateNode(node.id, { description: e.target.value })}
            placeholder="Enter description..."
            readOnly={readOnly}
          />
        </div>

        {/* Attributes Section */}
        <div className="space-y-2 pt-2 border-t border-slate-100">
          <div className="flex items-center justify-between">
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Attributes</label>
            <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full">{data.attributes.length}</span>
          </div>
          
          <div className="space-y-2">
            {data.attributes.map((attr, idx) => (
              <div key={idx} className="flex gap-2 group">
                <input
                   className="flex-1 p-2 text-xs border border-slate-200 rounded-md focus:border-teal-500 outline-none"
                   value={attr.role}
                   placeholder="Key"
                   onChange={(e) => handleAttrChange(idx, 'role', e.target.value)}
                   readOnly={readOnly}
                />
                <input
                   className="flex-1 p-2 text-xs border border-slate-200 rounded-md focus:border-teal-500 outline-none"
                   value={attr.value}
                   placeholder="Value"
                   onChange={(e) => handleAttrChange(idx, 'value', e.target.value)}
                   readOnly={readOnly}
                />
                {!readOnly && (
                    <button onClick={() => removeAttr(idx)} className="p-2 text-slate-300 hover:text-red-500 transition-colors">
                        <X size={14} />
                    </button>
                )}
              </div>
            ))}
            
            {!readOnly && (
                <button 
                    onClick={addAttr} 
                    className="w-full p-2 border border-slate-200 border-dashed rounded-md bg-slate-50 flex items-center justify-center gap-2 text-slate-500 hover:bg-slate-100 hover:text-teal-600 transition-colors text-xs font-medium"
                >
                    <Plus size={14} />
                    Add Attribute
                </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const StatusTab: React.FC<Omit<NodeInspectorProps, 'onClose'>> = ({ node, readOnly, onUpdateNode }) => {
    if (!node) return null;
    const data = node.data;

    return (
        <div className="p-5 h-full overflow-y-auto">
            <div className="space-y-6">
                
                {/* Active Toggle */}
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-100">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-slate-900 font-bold">Active Status</span>
                        <button 
                            disabled={readOnly}
                            onClick={() => onUpdateNode(node.id, { isActive: !data.isActive })}
                            className={`transition-colors ${data.isActive ? 'text-emerald-600' : 'text-slate-300'}`}
                        >
                            {data.isActive ? <ToggleRight size={32} /> : <ToggleLeft size={32} />}
                        </button>
                    </div>
                    <p className="text-xs text-slate-500 leading-relaxed">
                        Controls whether this group is currently active in the system. Inactive groups are hidden from most standard views.
                    </p>
                </div>

                {/* Suspended Section */}
                <div className={`rounded-lg p-4 border transition-colors ${data.isSuspended ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-100'}`}>
                    <div className="flex items-center justify-between mb-3">
                         <div className="flex items-center gap-2">
                             <PauseCircle size={18} className={data.isSuspended ? 'text-amber-600' : 'text-slate-400'} />
                             <span className={`text-sm font-bold ${data.isSuspended ? 'text-amber-800' : 'text-slate-700'}`}>Suspended</span>
                         </div>
                         <button 
                            disabled={readOnly}
                            onClick={() => onUpdateNode(node.id, { isSuspended: !data.isSuspended })}
                            className={`transition-colors ${data.isSuspended ? 'text-amber-600' : 'text-slate-300'}`}
                         >
                            {data.isSuspended ? <ToggleRight size={32} /> : <ToggleLeft size={32} />}
                         </button>
                    </div>
                    
                    {data.isSuspended && (
                        <div className="space-y-3 animate-in fade-in slide-in-from-top-2">
                            <div>
                                <label className="text-[10px] font-bold text-amber-700/70 uppercase tracking-wider">Suspension Reason</label>
                                <input 
                                    className="w-full mt-1 p-2 text-xs border border-amber-200 rounded bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-amber-500"
                                    placeholder="e.g. Under Investigation"
                                    value={data.suspensionReason || ''}
                                    onChange={(e) => onUpdateNode(node.id, { suspensionReason: e.target.value })}
                                    readOnly={readOnly}
                                />
                            </div>
                            <div>
                                <label className="text-[10px] font-bold text-amber-700/70 uppercase tracking-wider">Additional Message</label>
                                <textarea 
                                    className="w-full mt-1 p-2 text-xs border border-amber-200 rounded bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-amber-500 resize-none h-16"
                                    placeholder="Details about suspension..."
                                    value={data.suspensionMessage || ''}
                                    onChange={(e) => onUpdateNode(node.id, { suspensionMessage: e.target.value })}
                                    readOnly={readOnly}
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* Deleted Section */}
                <div className={`rounded-lg p-4 border transition-colors ${data.isDeleted ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-100'}`}>
                    <div className="flex items-center justify-between mb-3">
                         <div className="flex items-center gap-2">
                             <Trash2 size={18} className={data.isDeleted ? 'text-red-600' : 'text-slate-400'} />
                             <span className={`text-sm font-bold ${data.isDeleted ? 'text-red-800' : 'text-slate-700'}`}>Deleted (Soft)</span>
                         </div>
                         <button 
                            disabled={readOnly}
                            onClick={() => onUpdateNode(node.id, { isDeleted: !data.isDeleted })}
                            className={`transition-colors ${data.isDeleted ? 'text-red-600' : 'text-slate-300'}`}
                         >
                            {data.isDeleted ? <ToggleRight size={32} /> : <ToggleLeft size={32} />}
                         </button>
                    </div>

                    {data.isDeleted && (
                        <div className="space-y-3 animate-in fade-in slide-in-from-top-2">
                            <div>
                                <label className="text-[10px] font-bold text-red-700/70 uppercase tracking-wider">Deletion Reason</label>
                                <input 
                                    className="w-full mt-1 p-2 text-xs border border-red-200 rounded bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-red-500"
                                    placeholder="e.g. Department Closure"
                                    value={data.deletionReason || ''}
                                    onChange={(e) => onUpdateNode(node.id, { deletionReason: e.target.value })}
                                    readOnly={readOnly}
                                />
                            </div>
                            <div>
                                <label className="text-[10px] font-bold text-red-700/70 uppercase tracking-wider">Additional Message</label>
                                <textarea 
                                    className="w-full mt-1 p-2 text-xs border border-red-200 rounded bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-red-500 resize-none h-16"
                                    placeholder="Details about deletion..."
                                    value={data.deletionMessage || ''}
                                    onChange={(e) => onUpdateNode(node.id, { deletionMessage: e.target.value })}
                                    readOnly={readOnly}
                                />
                            </div>
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
}

const MembersTab: React.FC<{ node: Node<NodeData> | null, readOnly: boolean }> = ({ node }) => {
  const { onFetchMembers } = useEditorContext();
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState<Member[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [isInclusive, setIsInclusive] = useState(false);

  useEffect(() => {
    if (!node) return;
    let mounted = true;
    setLoading(true);

    const timer = setTimeout(() => {
       if (onFetchMembers) {
           onFetchMembers(node.id, node.data.name, page, search, isInclusive).then(res => {
             if (mounted) {
               setMembers(res.data);
               setTotal(res.total);
               setLoading(false);
             }
           });
       } else {
           if (mounted) {
               setLoading(false);
               setMembers([]);
               setTotal(0);
           }
       }
    }, 300); // Debounce

    return () => { mounted = false; clearTimeout(timer); };
  }, [node, page, search, isInclusive, onFetchMembers]);

  if (!node) return null;

  return (
    <div className="flex flex-col h-full">
        <div className="p-4 border-b border-slate-100 space-y-3">
            {/* View Toggle */}
            <div className="flex p-1 bg-slate-100 rounded-lg">
                <button 
                    onClick={() => { setIsInclusive(false); setPage(1); }}
                    className={`flex-1 flex items-center justify-center gap-2 py-1.5 text-xs font-bold rounded-md transition-all ${!isInclusive ? 'bg-white text-teal-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                    <User size={14} />
                    Direct Members
                </button>
                <button 
                    onClick={() => { setIsInclusive(true); setPage(1); }}
                    className={`flex-1 flex items-center justify-center gap-2 py-1.5 text-xs font-bold rounded-md transition-all ${isInclusive ? 'bg-white text-teal-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                    <Layers size={14} />
                    Inclusive
                </button>
            </div>

            {/* Search Bar */}
            <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input 
                    className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 transition-all"
                    placeholder="Search members..."
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                />
            </div>
        </div>

        {/* Member List */}
        <div className="flex-1 overflow-y-auto">
            {loading ? (
                <div className="flex flex-col items-center justify-center h-40 gap-3 text-slate-400">
                    <Loader2 size={24} className="animate-spin text-teal-500" />
                    <span className="text-xs font-medium">Loading members...</span>
                </div>
            ) : members.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-40 gap-2 text-slate-400">
                    <Users size={24} />
                    <span className="text-xs">No members found</span>
                </div>
            ) : (
                <div className="divide-y divide-slate-100">
                    {members.map(member => (
                        <div key={member.id} className="p-4 hover:bg-slate-50 transition-colors flex items-center justify-between group">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center font-bold text-xs shrink-0">
                                    {member.name.substring(0,2).toUpperCase()}
                                </div>
                                <div className="min-w-0">
                                    <div className="text-sm font-medium text-slate-800 truncate">{member.name}</div>
                                    <div className="text-[10px] text-slate-500 truncate">{member.email}</div>
                                    
                                    {/* Source Group Badge for Inclusive View */}
                                    {isInclusive && member.sourceGroup && (
                                        <div className="flex items-center gap-1 mt-1">
                                             <span className="text-[9px] text-slate-400">from</span>
                                             <span className="text-[9px] font-medium text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200 truncate max-w-[200px]" title={member.sourceGroup}>
                                                {member.sourceGroup}
                                             </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="flex flex-col items-end gap-1 shrink-0 ml-2">
                                <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider ${
                                    member.status === 'Active' ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-500'
                                }`}>
                                    {member.status}
                                </span>
                                <span className="text-[10px] text-slate-400 font-mono">{member.role}</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>

        {/* Pagination */}
        <div className="p-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between shrink-0">
            <span className="text-xs text-slate-500">
                {(page-1)*10 + 1}-{Math.min(page*10, total)} of {total}
            </span>
            <div className="flex gap-1">
                <button 
                    onClick={() => setPage(p => Math.max(1, p-1))}
                    disabled={page === 1 || loading}
                    className="p-1.5 hover:bg-white rounded border border-transparent hover:border-slate-200 disabled:opacity-30 disabled:hover:bg-transparent"
                >
                    <ChevronLeft size={16} className="text-slate-600" />
                </button>
                <button 
                    onClick={() => setPage(p => p+1)}
                    disabled={page * 10 >= total || loading}
                    className="p-1.5 hover:bg-white rounded border border-transparent hover:border-slate-200 disabled:opacity-30 disabled:hover:bg-transparent"
                >
                    <ChevronRight size={16} className="text-slate-600" />
                </button>
            </div>
        </div>
    </div>
  );
}

export default NodeInspector;
